// Organização do cliente: Empresas, Projetos e Usuários.
//
// Empresa ──< Projeto ──< Chamado          Empresa ──< Usuário (perfil cliente)
//    │            │                                        │
//    └ sistemas   └ sistemas ⊆ os da empresa      acesso N:N a projetos
//
// O escopo de visibilidade do cliente é o projeto: ele só enxerga chamados dos
// projetos aos quais tem acesso.
import { z } from 'zod';
import { PROJECT_STATUS, USER_ROLES } from './enums.js';
/* ── Empresas ────────────────────────────────────────────────────────────── */
/** Aceita com ou sem máscara; guarda só os dígitos. */
export const cnpjSchema = z
    .string()
    .trim()
    .transform((v) => v.replace(/\D/g, ''))
    .refine((v) => v.length === 0 || v.length === 14, 'O CNPJ precisa ter 14 dígitos');
export const companyInputSchema = z.object({
    name: z.string().trim().min(2).max(120),
    legalName: z.string().trim().max(160).optional().default(''),
    cnpj: cnpjSchema.optional().default(''),
    segment: z.string().trim().max(80).optional().default(''),
    contactName: z.string().trim().max(120).optional().default(''),
    contactRole: z.string().trim().max(80).optional().default(''),
    contactEmail: z
        .union([z.literal(''), z.string().email()])
        .optional()
        .default(''),
    contactPhone: z.string().trim().max(40).optional().default(''),
    /** Soluções contratadas: limita o que os projetos da empresa podem usar. */
    systemIds: z.array(z.string().uuid()).default([]),
    active: z.boolean().default(true),
});
export const COMPANY_SORTABLE = [
    'name',
    'cnpj',
    'segment',
    'projectCount',
    'userCount',
    'active',
    'createdAt',
];
/* ── Projetos ────────────────────────────────────────────────────────────── */
export const projectInputSchema = z.object({
    name: z.string().trim().min(2).max(120),
    companyId: z.string().uuid(),
    description: z.string().trim().max(2000).optional().default(''),
    ownerUserId: z.string().uuid().nullable().default(null),
    startsOn: z.string().date().nullable().optional().default(null),
    status: z.enum(PROJECT_STATUS).default('em_implantacao'),
    /** Precisa ser subconjunto dos sistemas contratados pela empresa. */
    systemIds: z.array(z.string().uuid()).default([]),
    /** Usuários cliente da empresa que podem abrir chamado neste projeto. */
    userIds: z.array(z.string().uuid()).default([]),
});
export const PROJECT_SORTABLE = [
    'name',
    'companyName',
    'ownerName',
    'startsOn',
    'status',
    'userCount',
    'ticketCount',
    'createdAt',
];
export const PROJECT_STATUS_LABEL = {
    em_implantacao: 'Em implantação',
    em_producao: 'Em produção',
    pausado: 'Pausado',
    encerrado: 'Encerrado',
};
/* ── Usuários ────────────────────────────────────────────────────────────── */
export const userInputSchema = z
    .object({
    name: z.string().trim().min(2).max(120),
    email: z.string().trim().toLowerCase().email(),
    phone: z.string().trim().max(40).optional().default(''),
    role: z.enum(USER_ROLES),
    /** "Gestor do suporte": nível final do escalonamento e alvo dos alertas de
     *  SLA. Só faz sentido em administrador. */
    isSupportManager: z.boolean().default(false),
    /** Obrigatória para o perfil cliente; nula para os perfis internos. */
    companyId: z.string().uuid().nullable().default(null),
    projectIds: z.array(z.string().uuid()).default([]),
    active: z.boolean().default(true),
})
    .refine((v) => v.role !== 'cliente' || v.companyId !== null, {
    message: 'Usuário cliente precisa estar vinculado a uma empresa',
    path: ['companyId'],
})
    .refine((v) => v.role === 'cliente' || v.companyId === null, {
    message: 'Usuário interno não é vinculado a uma empresa',
    path: ['companyId'],
})
    .refine((v) => !v.isSupportManager || v.role === 'administrador', {
    message: 'Só um administrador pode ser gestor do suporte',
    path: ['isSupportManager'],
});
export const USER_SORTABLE = [
    'name',
    'email',
    'role',
    'companyName',
    'lastLoginAt',
    'active',
    'createdAt',
];
export const USER_ROLE_LABEL = {
    cliente: 'Cliente',
    tecnico: 'Técnico',
    administrador: 'Administrador',
};
